import { useEffect, useRef, useState } from 'react';
import { Icon } from '../ui';
import { lineCount, lookFor, type ProgressStep, summarize, toLines } from '../utils/progress';
import { isKbUrl } from '../utils/support';
import styles from './ProgressLog.module.css';

interface Props {
  steps: ProgressStep[];
  /** True while the assistant is still working: the log is open and its last line live. */
  live: boolean;
  /** Opens a finding's section; without it the link opens in a new tab. */
  onOpenLink?: (url: string) => void;
}

/**
 * What the assistant did while answering, as a log (#474). While it works, every step
 * is a line, newest last. Once the answer starts, the log folds into one summary line
 * that opens on click; a reloaded conversation shows the same folded line.
 */
export default function ProgressLog({ steps, live, onOpenLink }: Props) {
  const [open, setOpen] = useState(false);
  const listRef = useRef<HTMLOListElement>(null);

  const lines = toLines(steps);
  // Keep the newest line in view while the log grows.
  const count = steps.length;
  useEffect(() => {
    if (live && count > 0 && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [live, count]);

  if (steps.length === 0) return null;

  const list = (
    <ol ref={listRef} className={`${styles.log}${live ? ` ${styles.logLive}` : ''}`}>
      {lines.map((line, i) => {
        const { step } = line;
        const look = lookFor(step.kind);
        const last = live && i === lines.length - 1;
        const counted = lineCount(line);
        return (
          <li
            // biome-ignore lint/suspicious/noArrayIndexKey: lines only ever append or grow
            key={i}
            className={`${styles.line}${look.found ? ` ${styles.found}` : ''}${last ? ` ${styles.current}` : ''}`}
          >
            <Icon name={look.icon} className={styles.icon} />
            <span>
              <span className={styles.label}>{step.label}</span>
              {step.detail && <span className={styles.detail}> {step.detail}</span>}
              {counted && <span className={styles.detail}> {counted}</span>}
              {step.link && (
                <a
                  className={styles.link}
                  href={step.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    // A Customer Care article keeps the plain new-tab link.
                    if (!onOpenLink || !step.link || isKbUrl(step.link)) return;
                    e.preventDefault();
                    onOpenLink(step.link);
                  }}
                >
                  {isKbUrl(step.link) ? 'Open article' : 'Open section'}
                </a>
              )}
              {step.note && <span className={styles.note}>{step.note}</span>}
            </span>
          </li>
        );
      })}
    </ol>
  );

  if (live) {
    return (
      <div className={styles.wrap}>
        <div className={styles.head}>
          <span className={styles.dot} aria-hidden="true" />
          Working on it
        </div>
        {list}
      </div>
    );
  }

  return (
    <div className={styles.wrap}>
      <button
        type="button"
        className={styles.fold}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="check-circle" className={styles.foldIcon} />
        <span>
          Checked <span className={styles.summary}>{summarize(steps)}</span>
        </span>
        <Icon name={open ? 'chevron-up' : 'chevron-down'} className={styles.chevron} />
      </button>
      {open && list}
    </div>
  );
}
